import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { ShieldCheck, Truck, Package } from 'lucide-react';
import axios from 'axios';
import './CheckoutPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { formatPrice, getCurrencyCode } = useCurrency();

  const [shippingOption, setShippingOption] = useState('regular');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Get product data from navigation state
  const { product, addKeepsakeTin, startMonth } = location.state || {};

  useEffect(() => {
    document.title = 'Checkout | Binding Thread';
    const link = document.querySelector("link[rel='canonical']") || document.createElement('link');
    link.rel = 'canonical';
    link.href = 'https://www.bindingthread.com.au/checkout';
    if (!document.querySelector("link[rel='canonical']")) document.head.appendChild(link);
    if (!product) {
      navigate('/shop');
    }
  }, [product, navigate]);

  if (!product) return null;

  const needsShipping = addKeepsakeTin || product.id === 'keepsake-tin';
  const shippingCost = needsShipping ? (shippingOption === 'express' ? 20 : 16) : 0;

  const itemTotal = addKeepsakeTin ? product.price + 44.95 : product.price;
  const orderTotal = itemTotal + shippingCost;

  const handlePay = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setCheckoutLoading(true);
    try {
      const currency = getCurrencyCode().toLowerCase();
      const response = await axios.post(
        `${API_URL}/api/checkout/create-session`,
        {
          product_id: product.id,
          origin_url: window.location.origin,
          add_keepsake_tin: addKeepsakeTin || false,
          start_month: startMonth || null,
          currency: currency,
          shipping_option: needsShipping ? shippingOption : null
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <div className="checkout-container">
        <h1>Checkout</h1>

        <div className="checkout-card">
          {/* Order Items */}
          <div className="order-section">
            <h2>Your Order</h2>
            <div className="order-item">
              <div className="order-item-info">
                <Package size={20} />
                <div>
                  <span className="item-name">{product.name}</span>
                  {product.interval && (
                    <span className="item-interval">/{product.interval === 'month' ? 'mo' : 'yr'}</span>
                  )}
                </div>
              </div>
              <span className="item-price">{formatPrice(product.price, true)} {getCurrencyCode()}</span>
            </div>
            {addKeepsakeTin && product.id !== 'keepsake-tin' && (
              <div className="order-item">
                <div className="order-item-info">
                  <Package size={20} />
                  <span className="item-name">Keepsake Tin</span>
                </div>
                <span className="item-price">{formatPrice(44.95, true)} {getCurrencyCode()}</span>
              </div>
            )}
          </div>

          {/* Shipping Options */}
          {needsShipping && (
            <div className="order-section" data-testid="shipping-section">
              <h2><Truck size={20} /> Shipping</h2>
              <div className="shipping-options">
                <label
                  className={`shipping-choice ${shippingOption === 'regular' ? 'active' : ''}`}
                  data-testid="shipping-regular"
                >
                  <input
                    type="radio"
                    name="shipping"
                    value="regular"
                    checked={shippingOption === 'regular'}
                    onChange={() => setShippingOption('regular')}
                  />
                  <div className="shipping-choice-content">
                    <span className="shipping-name">Regular Postage</span>
                    <span className="shipping-detail">Standard delivery</span>
                  </div>
                  <span className="shipping-price">{formatPrice(16, true)} {getCurrencyCode()}</span>
                </label>
                <label
                  className={`shipping-choice ${shippingOption === 'express' ? 'active' : ''}`}
                  data-testid="shipping-express"
                >
                  <input
                    type="radio"
                    name="shipping"
                    value="express"
                    checked={shippingOption === 'express'}
                    onChange={() => setShippingOption('express')}
                  />
                  <div className="shipping-choice-content">
                    <span className="shipping-name">Express Post</span>
                    <span className="shipping-detail">Priority delivery</span>
                  </div>
                  <span className="shipping-price">{formatPrice(20, true)} {getCurrencyCode()}</span>
                </label>
              </div>
            </div>
          )}

          {/* Order Total */}
          <div className="order-total">
            {needsShipping && (
              <>
                <div className="total-line subtotal">
                  <span>Subtotal</span>
                  <span>{formatPrice(itemTotal, true)} {getCurrencyCode()}</span>
                </div>
                <div className="total-line subtotal">
                  <span>Shipping</span>
                  <span>{formatPrice(shippingCost, true)} {getCurrencyCode()}</span>
                </div>
              </>
            )}
            <div className="total-line grand-total">
              <span>Total</span>
              <span>{formatPrice(orderTotal, true)} {getCurrencyCode()}</span>
            </div>
          </div>

          {/* Terms & Pay */}
          <div className="checkout-pay-section">
            <label className="terms-agree" data-testid="terms-checkbox-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                data-testid="terms-checkbox"
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank">Terms of Service</Link> and{' '}
                <Link to="/privacy" target="_blank">Privacy Policy</Link>
              </span>
            </label>
            <button
              className="pay-button"
              onClick={handlePay}
              disabled={checkoutLoading || !agreedToTerms}
              data-testid="pay-btn"
            >
              {checkoutLoading ? 'Processing...' : `Pay ${formatPrice(orderTotal, true)} ${getCurrencyCode()}`}
            </button>
            <div className="secure-note">
              <ShieldCheck size={14} />
              <span>Secure payment powered by Stripe</span>
            </div>
          </div>
        </div>

        <button className="back-link" onClick={() => navigate('/shop')}>
          &larr; Back to Shop
        </button>
      </div>
    </div>
  );
}
